import camelCase from 'lodash/camelCase';
import keyBy from 'lodash/keyBy';
import uniq from 'lodash/uniq';
import { ThemeSubtype, ThemeType } from '@/creditflowresearch/domain/vo/ArticlesAndStats/Articles/themeTypes';
import { AbsProduct, HycProduct, IgcProduct } from '@/creditflowresearch/domain/Product';
import { ITabSubtypeItem, ITabTypeItem } from '@/creditflowresearch/domain/vo/ArticlesAndStats/Articles/ITabItem';
import { IProduct } from '@/creditflowresearch/domain/vo/Product';
import { IMarketTheme } from '@/creditflowresearch/domain/vo/ArticlesAndStats/IMarketTheme';
import { IRegion } from '@/creditflowresearch/domain/vo/Region';
import { IMarketArticlesFilterPayload } from '@/creditflowresearch/domain/vo/ArticlesAndStats/Articles/IMarketArticle';
import { TCheckboxesState } from '@/creditflowresearch/domain/vo/TCheckboxesState';
import { mergeCheckedIdsToParent } from '@framework/ui/Organisms/Filters/helpers';
import { IRegion as IRegionFilters } from '@/creditflowresearch/domain/vo/Filters/IRegion';
import { EmergingMarket } from '@/creditflowresearch/domain/Market';

export const mapType: Record<ThemeType, { url: string, label: string }> = {
  [ThemeType.all]: { url: 'commentary-deal-flow', label: 'All' },
  [ThemeType.commentary]: { url: 'commentary', label: 'Commentary' },
  [ThemeType.dealFlow]: { url: 'deal-flow', label: 'Deal Flow' },
};

export const mapSubtype: Record<ThemeSubtype, { url: string, label: string }> = {
  [ThemeSubtype.all]: { url: 'all', label: 'All' },
  [ThemeSubtype.abs]: { url: 'abs', label: 'ABS' },
  [ThemeSubtype.clo]: { url: 'clo', label: 'CLO' },
  [ThemeSubtype.cmbs]: { url: 'cmbs', label: 'CMBS' },
  [ThemeSubtype.rmbs]: { url: 'rmbs', label: 'RMBS' },
  [ThemeSubtype.close]: { url: 'close', label: 'Close' },
  [ThemeSubtype.general]: { url: 'general', label: 'General' },
  [ThemeSubtype.mAndA]: { url: 'm-and-a', label: 'M&A' },
  [ThemeSubtype.midday]: { url: 'midday', label: 'Midday' },
  [ThemeSubtype.open]: { url: 'open', label: 'Open' },
  [ThemeSubtype.openAndClose]: { url: 'open-and-close', label: 'Open & Close' },
  [ThemeSubtype.retail]: { url: 'retail', label: 'Retail' },
  [ThemeSubtype.ssa]: { url: 'ssa', label: 'SSA' },
  [ThemeSubtype.tenders]: { url: 'tenders', label: 'Tenders' },
};

const mapTypeTab = Object.keys(ThemeType).reduce((acc, key) => {
  const { url, label } = mapType[key];

  acc[key] = {
    label,
    baseUrl: `/${ url }`,
    url: `/${ url }-${ ThemeSubtype.all }`,
    key: camelCase(url),
  };

  return acc;
}, {});

const getMapSubtypeTab = (type: ThemeType) => Object.keys(ThemeSubtype).reduce((acc, key) => {
  const { url, label } = mapSubtype[key];

  acc[key] = {
    label,
    url: `${ mapTypeTab[type].baseUrl }-${ url }`,
    key: camelCase(`${ mapTypeTab[type].key }-${ url }`),
  };

  return acc;
}, {});

export const getTypeTabsConfig = (): ITabTypeItem[] => [
  mapTypeTab[ThemeType.all],
  mapTypeTab[ThemeType.commentary],
  mapTypeTab[ThemeType.dealFlow],
];

type ISubtypeRule = {
  match: (productId: string | null, type: ThemeType, markets: string[]) => boolean,
  subtypes: ThemeSubtype[],
};

const subtypeRules: ISubtypeRule[] = [
  {
    match: (_, type, markets = []) => markets.includes(EmergingMarket.ABBREVIATION) && type === ThemeType.commentary,
    subtypes: [
      ThemeSubtype.all,
      ThemeSubtype.general,
      ThemeSubtype.open,
      ThemeSubtype.midday,
      ThemeSubtype.close,
      ThemeSubtype.mAndA,
    ],
  },
  {
    match: (_, type, markets = []) => markets.includes(EmergingMarket.ABBREVIATION) && type === ThemeType.dealFlow,
    subtypes: [
      ThemeSubtype.all,
      ThemeSubtype.general,
      ThemeSubtype.retail,
      ThemeSubtype.ssa,
      ThemeSubtype.tenders,
    ],
  },
  {
    match: (productId) => productId === AbsProduct.PRODUCT_ID,
    subtypes: [
      ThemeSubtype.all,
      ThemeSubtype.abs,
      ThemeSubtype.clo,
      ThemeSubtype.cmbs,
      ThemeSubtype.rmbs,
    ],
  },
  {
    match: (productId, type) => productId === HycProduct.PRODUCT_ID && type === ThemeType.commentary,
    subtypes: [
      ThemeSubtype.all,
      ThemeSubtype.general,
      ThemeSubtype.open,
      ThemeSubtype.midday,
      ThemeSubtype.close,
      ThemeSubtype.openAndClose,
      ThemeSubtype.mAndA,
    ],
  },
  {
    match: (productId, type) => productId === HycProduct.PRODUCT_ID && type === ThemeType.dealFlow,
    subtypes: [
      ThemeSubtype.all,
      ThemeSubtype.general,
      ThemeSubtype.retail,
      ThemeSubtype.tenders,
    ],
  },
  {
    match: (productId, type) => productId === IgcProduct.PRODUCT_ID && type === ThemeType.commentary,
    subtypes: [
      ThemeSubtype.all,
      ThemeSubtype.general,
      ThemeSubtype.open,
      ThemeSubtype.close,
      ThemeSubtype.openAndClose,
      ThemeSubtype.mAndA,
    ],
  },
  {
    match: (productId, type) => productId === IgcProduct.PRODUCT_ID && type === ThemeType.dealFlow,
    subtypes: [
      ThemeSubtype.all,
      ThemeSubtype.general,
      ThemeSubtype.retail,
      ThemeSubtype.ssa,
      ThemeSubtype.tenders,
    ],
  },
];

export const getSubtypeTabsConfig = ({
  productIds = [],
  markets,
  type,
}: { productIds: string[], markets: string[], type: ThemeType }): ITabSubtypeItem[] => {
  const mapSubtypeTab = getMapSubtypeTab(type);
  const productId = productIds.length === 1 ? productIds[0] : null;
  const rule = subtypeRules.find(({ match }) => match(productId, type, markets));

  return rule?.subtypes.map((subtype) => mapSubtypeTab[subtype]) ?? [];
};

export const getProductsFromThemeIds = (
  themesDictionary: IMarketTheme[],
  themeIds: string[],
  productsDictionary: IProduct[],
): IProduct[] => {
  const groupedThemesById = keyBy(themesDictionary, 'id');
  const productIds = uniq(themeIds.map((themeId) => groupedThemesById[themeId]?.productId));

  return productsDictionary.filter((product) => productIds.includes(product.id));
};

export const getRegionsIdFromThemes = (
  themesDictionary: IMarketTheme[],
  themeIds: string[],
): string[] => {
  const groupedThemesById = keyBy(themesDictionary, 'id');
  const regionIds = themeIds.map((themeId) => groupedThemesById[themeId]?.regionId);

  return uniq(regionIds);
};

export const getRegionsByIdsFromDictionary = (
  regionIds: string[],
  regionsDictionary: IRegion[],
): IRegion[] => regionsDictionary.filter((region) => regionIds.includes(region.id));

interface IFiltersPayloadProps {
  filters: IMarketArticlesFilterPayload,
  checkboxesState: TCheckboxesState,
}

export const getFiltersPayload = ({
  filters,
  checkboxesState,
}: IFiltersPayloadProps) => {
  const sectorIds = mergeCheckedIdsToParent(
    checkboxesState,
    filters,
    'sectorList',
    'includedSubsectorIds',
  );

  const industryIds = mergeCheckedIdsToParent(
    checkboxesState,
    filters,
    'industryList',
    'includedSubindustryIds',
  );

  const regionIds = mergeCheckedIdsToParent(
    checkboxesState,
    filters,
    'regionList',
    'includedCountryIds',
  );

  return {
    ...filters,
    includedSectorIds: [...sectorIds.parentIds, ...filters.includedSectorIds],
    includedSubsectorIds: sectorIds.childrenIds,
    includedIndustryIds: [...industryIds.parentIds, ...filters.includedIndustryIds],
    includedSubindustryIds: industryIds.childrenIds,
    includedRegionIds: [...regionIds.parentIds, ...filters.includedRegionIds],
    includedCountryIds: regionIds.childrenIds,
  };
};

export const getRegionsByCountries = (regionList: IRegionFilters[], countryIds: string[]) => {
  return regionList.reduce((acc, region) => {
    if (region.countryList.find((country) => countryIds.includes(country.countryId))) {
      acc.push(region.regionId);
    }

    return acc;
  }, []);
};
